<template>
  <div
    v-show="innerVisible"
    class="km-modal__wrapper"
    :class="modalWrapperCss"
    @click.self="handleBackdropClick"
  >
    <div
      ref="modal"
      :key="key"
      class="km-modal"
      :class="modalCss"
      :style="modalStyle"
    >
      <div class="km-modal__header">
        <div
          v-if="title"
          class="km-modal__title"
        >
          <k-icon
            v-if="icon"
            :class="titleIconCss"
            :circle="!isMessageBox"
            :icon="icon"
          />
          <slot name="title">
            <div class="km-modal__title-inner">
              {{ title }}
            </div>
          </slot>
        </div>
        <k-button
          v-if="showClose && !plain"
          class="km-modal__close-btn"
          type="light"
          size="large"
          circle
          plain
          @click="handleClose"
        >
          <i class="km-modal__close-icon" />
          <!--            <k-icon icon="times" />-->
        </k-button>
      </div>
      <div
        v-if="rendered && scrollableScene"
        class="km-modal__body"
      >
        <k-scrollbar view-class="km-modal__scroll-view">
          <k-button
            v-if="showClose && plain"
            class="km-modal__close-btn"
            type="light"
            size="large"
            circle
            plain
            @click="handleClose"
          >
            <k-icon icon="times" />
          </k-button>
          <slot />
        </k-scrollbar>
      </div>
      <div
        v-else-if="rendered"
        class="km-modal__body"
      >
        <k-button
          v-if="showClose && plain"
          class="km-modal__close-btn"
          type="light"
          size="large"
          circle
          plain
          @click="handleClose"
        >
          <k-icon icon="times" />
        </k-button>
        <slot />
      </div>
      <div
        v-if="$slots.footer"
        class="km-modal__footer"
      >
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import KUuid from '../../core/utils/KUuid'
import KNextZIndex from '../../core/utils/KNextZIndex'
import { getCorrectEventName } from '../../core/utils'
import { window, document } from 'ssr-window'
import ModalManager from './ModalManager'

const animationEnd = getCorrectEventName(window, 'animationend')

export default {
  name: 'KModal',
  model: {
    prop: 'visible',
    event: 'visible',
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 标题
    title: {
      validator() {
        return true
      },
      default: '',
    },
    // 标题图标
    icon: {
      type: String,
      default: '',
    },
    // 主题色
    type: {
      type: String,
      default: 'primary',
    },
    width: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: 'auto',
    },
    size: {
      type: String,
      default: '', // large normal small mini full
    },
    // 当[按下esc]，[点击关闭按钮]，[点击遮罩]这三种方式触发 Modal 关闭时，会触发该函数
    // 函数传递一个 done函数 做为参数，主动调用该函数才会关闭
    beforeClose: {
      type: Function,
      default: undefined,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    plain: {
      type: Boolean,
      default: false,
    },
    // 点击 esc 是否关闭模态
    keyboard: {
      type: Boolean,
      default: false,
    },
    closeOnClickBackdrop: {
      type: Boolean,
      default: true,
    },
    // 滚动条在 modal__body 内
    scrollable: {
      type: Boolean,
      default: false,
    },
    // 垂直居中显示
    verticalCentered: {
      type: Boolean,
      default: false,
    },
    // 使用 transition
    transition: {
      type: String,
      default: '',
    },
    // 是否属于消息框
    isMessageBox: {
      type: Boolean,
      default: false,
    },
    destroyOnHide: {
      type: Boolean,
      default: false,
    },
    // 是否展示遮罩
    mask: {
      type: Boolean,
      default: true,
    },
    // 位置
    placement: {
      type: String,
      default: 'center', // center, right, left
    },
  },
  data() {
    return {
      closed: false, // 已关闭
      rendered: false, // 默认 slot 已渲染
      innerVisible: false,
      isMounted: false,
      key: 0,
    }
  },
  computed: {
    id() {
      return 'km-modal-' + KUuid()
    },
    modalWrapperCss() {
      if (this.placement !== 'center') {
        return `km-modal__wrapper--${this.placement}`
      } else {
        return ''
      }
    },
    modalCss() {
      const css = []
      if (this.visible) {
        css.push(`km-modal--open`)
      }
      if (this.plain) {
        css.push(`km-modal--plain`)
      }
      if (this.verticalCentered) {
        css.push(`km-modal--v-centered`)
      }
      if (this.isMessageBox) {
        css.push(`km-modal--message-box`)
      }
      if (!this.plain) {
        const size = this.size || 'normal'
        css.push(`km-modal--${size}`)
      }
      if (this.placement !== 'center') {
        css.push(`km-modal--${this.placement}`)
      }
      if (!this.icon) {
        css.push(`km-modal--no-icon`)
      }
      if (this.scrollableScene) {
        css.push(`km-modal--scrollable`)
      }
      return css
    },
    titleIconCss() {
      return [`has-text-${this.type}`, `km-modal__title-icon`]
    },
    modalWidth() {
      if (this.width) {
        return this.width
      }

      if (this.plain) {
        return 'auto'
      }

      return null
    },
    modalStyle() {
      return {
        width: this.modalWidth,
        height: this.height,
      }
    },
    scrollableScene() {
      return this.scrollable || this.placement !== 'center'
    },
  },
  watch: {
    visible: {
      immediate: true,
      async handler(val) {
        if (val) {
          this.closed = false
          ModalManager.register(this)

          if (!this.rendered) {
            this.rendered = true

            this.$nextTick(() => {
              this.innerVisible = val
            })
          } else {
            this.innerVisible = val
          }
          // 等待 Modal 渲染完成
          await this.$nextTick()
          this.openModal()
          if (!this.$isServer) {
            // FIXME: 不要重复添加
            document.body.appendChild(this.$el)
          }
        } else {
          if (!this.isMounted) {
            return
          }
          this.closeModal()
          this.innerVisible = val
          if (this.destroyOnHide) {
            this.rendered = false
            await this.$nextTick()
            this.key++
          }
          ModalManager.deregister(this.id)
          this.closed = true
        }
      },
    },
    closed(val) {
      if (val) {
        this.$nextTick(() => {
          this.$emit('closed')
        })
      }
    },
  },
  beforeMount() {
    ModalManager.register(this)
  },
  mounted() {
    this.isMounted = true
  },
  beforeDestroy() {
    ModalManager.closeModal(this.id)
    ModalManager.deregister(this.id)
    $(this.$el).remove()
  },
  methods: {
    openModal() {
      ModalManager.openModal({
        id: this.id,
        zIndex: KNextZIndex(),
        mask: this.mask,
      })
    },
    closeModal() {
      ModalManager.closeModal(this.id)
    },
    handleBackdropClick() {
      if (this.closeOnClickBackdrop) {
        this.handleClose()
      } else {
        const $modal = $(this.$refs.modal)
        $modal.addClass('animation-shake').one(animationEnd, () => {
          $modal.removeClass('animation-shake')
        })
      }
    },
    handleClose() {
      if (!this.visible) {
        return
      }
      // API
      if (this.beforeClose && typeof this.beforeClose === 'function') {
        this.beforeClose(this.doneHide)
      } else {
        this.doneHide()
      }
    },
    doneHide() {
      // API - parameter as beforeClose
      this.$emit('visible', false)
    },
  },
}
</script>
